import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AppRemixIcon,
  AppInput,
  AppMultiSelect,
  AppSingleSelect,
  AppTextArea,
  AppInputNumber,
  AppDatePicker,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import { UnmountClosed } from 'react-collapse/lib/UnmountClosed';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import Translator from '../../../../components/helpers/Translator';
import colors from '../../../../../constants/colors';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getResourceManagerFilterInitialValues } from '../../../../../minions/forms';
import AuxSidebar from '../../../../Sidebar/AuxSidebar';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import { getState } from '../tools/resourceManagerTools';
import ResourceManagerCategorySelect from '../../../../components/form/Selects/ResourceManagerCategorySelect';
import ResourceManagerSelectPlants from '../../../../components/form/Selects/ResourceManagerSelectPlants';

export default function FilterSidebarBlock() {
  const location = useLocation();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { moduleBridgeHex, filtersHex } = useParams();
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const filters = useSelector((state) => state.currentPage?.filters);
  const [openOnce, setOpenOnce] = useState(true);
  const [fieldFilters, setFieldFilters] = useState(false);
  const [featureFilters, setFeatureFilters] = useState();
  const [view] = useState(
    location?.pathname?.includes('/resource/') ? 'resource' : 'obligation'
  );
  const [noYesOptions] = useState([
    {
      label: <Translator file="user" name="general.no" />,
      value: 0,
    },
    {
      label: <Translator file="user" name="general.yes" />,
      value: 1,
    },
  ]);
  const [stateOptions] = useState(
    // [-1, 0, 1, 2, 3, 10]?.map((s) => ({
    [-1, 1, 2, 3]?.map((s) => ({
      value: s,
      label: getState(s)?.title,
      circle: getState(s)?.bgColor,
    }))
  );

  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'filters')
      dispatch(currentPageAction.change('auxSidebar', { opened: 'filters' }));
    else dispatch(currentPageAction.change('auxSidebar', { opened: null }));
  };

  const goToEvaluation = (hexCode) =>
    navigate(
      path({
        name:
          view === 'resource'
            ? 'user_module_resourceManager_resource_home'
            : 'user_module_resourceManager_obligation_home',
        params: {
          moduleBridgeHex,
          page: 1,
          filtersHex: hexCode,
        },
      })
    );

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = async (values, setSubmitting) =>
    axios
      .post(
        axiosPath('api_r_user_module_resourceManager_generateFilterHexCode'),
        { moduleBridgeHex, filters: values }
      )
      .then(({ data }) => {
        setSubmitting(false);
        goToEvaluation(data.hexCode);
        dispatch(currentPageAction.change('auxSidebar', { opened: null }));
      });

  const handleOnSubmit = (values, { setSubmitting }) => {
    generateFilterHexCode(values, setSubmitting);
  };
  // < GENERATE FILTER HEX CODE

  // > FILTERS
  const getFilters = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_resourceManager_getFilters'), {
          moduleBridgeHex,
          filtersHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change('filters', {
                ...getResourceManagerFilterInitialValues(),
                ...data,
              })
            );
          }
        }),
    [moduleBridgeHex, filtersHex]
  );

  useEffect(() => {
    if (filtersHex !== '0') {
      getFilters();
    } else {
      dispatch(
        currentPageAction.change(
          'filters',
          getResourceManagerFilterInitialValues(),
          true
        )
      );
    }
  }, [filtersHex]);
  // < FILTERS

  // > LOAD FILTERS BY FEATURES
  const getFiltersByFeatures = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_resourceManager_getFiltersByFeatures'),
          { moduleBridgeHex }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setFeatureFilters(data);
          }
        }),
    [moduleBridgeHex]
  );

  useEffect(() => {
    if (view === 'resource') getFiltersByFeatures();
  }, []);
  // < LOAD FILTERS BY FEATURES

  const renderFeatureInputs = useCallback(
    (values, errors, setFieldValue) =>
      featureFilters?.map((f) => {
        switch (f?.type) {
          case 0:
            return (
              <React.Fragment key={f?.hex}>
                <AppInput
                  title={f?.name}
                  value={values?.[`feature${f?.hex}`] ?? ''}
                  error={errors?.[`feature${f?.hex}`]}
                  onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                  isClearable
                />
              </React.Fragment>
            );
          case 1:
            return (
              <React.Fragment key={f?.hex}>
                <AppTextArea
                  title={f?.name}
                  value={values?.[`feature${f?.hex}`] ?? ''}
                  error={errors?.[`feature${f?.hex}`]}
                  onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                  isClearable
                />
              </React.Fragment>
            );
          case 2:
            return (
              <React.Fragment key={f?.hex}>
                <AppInputNumber
                  title={f?.name}
                  value={values?.[`feature${f?.hex}`] ?? ''}
                  error={errors?.[`feature${f?.hex}`]}
                  onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                  decimals={f?.quantity > 0 ? f?.quantity : 99}
                  isClearable
                />
              </React.Fragment>
            );
          case 3:
            return (
              <React.Fragment key={f?.hex}>
                <AppDatePicker
                  title={f?.name}
                  value={values?.[`feature${f?.hex}`] ?? ''}
                  error={errors?.[`feature${f?.hex}`]}
                  onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                  isClearable
                  hasToggleType
                />
              </React.Fragment>
            );
          case 4:
            return (
              <React.Fragment key={f?.hex}>
                <AppSingleSelect
                  title={f?.name}
                  options={f?.options}
                  value={values?.[`feature${f?.hex}`] ?? null}
                  error={errors?.[`feature${f?.hex}`]}
                  onChange={(opt) => setFieldValue(`feature${f?.hex}`, opt)}
                  isClearable
                  hasSearchBox
                />
              </React.Fragment>
            );
          case 5:
            return (
              <React.Fragment key={f?.hex}>
                <AppMultiSelect
                  title={f?.name}
                  options={f?.options}
                  value={values?.[`feature${f?.hex}`] ?? []}
                  error={errors?.[`feature${f?.hex}`]}
                  onChange={(opt) => setFieldValue(`feature${f?.hex}`, opt)}
                  hasSearchBox
                  hasSelectOptions
                />
              </React.Fragment>
            );
          default:
            return null;
        }
      }),
    [featureFilters]
  );

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'filters' ? 'active' : ''}
        onClick={() => handleAuxSidebar()}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="equalizer" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="module-legalCompliance" name="filtering" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="module-legalCompliance" name="filtering" />}
        isOpen={auxSidebar && auxSidebar === 'filters'}
        width="350px"
        position="right"
        onClose={handleAuxSidebar}
      >
        {openOnce && (
          <>
            {filters && (
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
                initialValues={filters}
                onSubmit={handleOnSubmit}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  errors,
                  isSubmitting,
                  resetForm,
                }) => (
                  <>
                    <div className="d-flex mt-3 mb-3">
                      <span
                        className="btn btn-info btn-block"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="filtering"
                        />
                      </span>
                      {filtersHex !== '0' && (
                        <span
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="delete.filters"
                            />
                          )}
                          className="btn btn-outline-danger ml-1"
                          onClick={() => goToEvaluation(0)}
                        >
                          <AppRemixIcon icon="delete-bin-6" />
                        </span>
                      )}
                    </div>
                    {view === 'obligation' && (
                      <AppInput
                        title={
                          <Translator
                            file="module-resourceManager"
                            name="resource.obligations-code-input"
                          />
                        }
                        placeholder={
                          <Translator
                            file="module-legalCompliance"
                            name="split.by.commas"
                          />
                        }
                        icon={<AppRemixIcon icon="barcode" />}
                        value={values.obligationHex}
                        onChange={(opt) =>
                          setFieldValue(
                            'obligationHex',
                            /^([a-fA-F0-9]|,)*$/.test(opt)
                              ? opt
                              : values.obligationHex
                          )
                        }
                        isClearable
                      />
                    )}
                    <AppInput
                      title={
                        <Translator
                          file="module-resourceManager"
                          name="resource.code.assets"
                        />
                      }
                      placeholder={
                        <Translator
                          file="module-legalCompliance"
                          name="split.by.commas"
                        />
                      }
                      icon={<AppRemixIcon icon="barcode" />}
                      value={values.resourceHex}
                      onChange={(opt) =>
                        setFieldValue(
                          'resourceHex',
                          /^([a-fA-F0-9]|,)*$/.test(opt)
                            ? opt
                            : values.resourceHex
                        )
                      }
                      isClearable
                    />
                    <AppInput
                      title={
                        <Translator
                          file="module-resourceManager"
                          name="resource.name"
                        />
                      }
                      icon={<AppRemixIcon icon="font-color" noVariants />}
                      value={values.name}
                      onChange={(opt) => setFieldValue('name', opt)}
                      isClearable
                    />
                    <ResourceManagerCategorySelect
                      value={values.categories}
                      onHandleChange={(v) => setFieldValue('categories', v)}
                      hasSearchBox
                      hasSelectOptions
                    />
                    {view === 'resource' && (
                      <>
                        <ResourceManagerSelectPlants
                          value={values.plants}
                          onHandleChange={(v) => setFieldValue('plants', v)}
                          hasSearchBox
                          hasSelectOptions
                        />
                        <AppSingleSelect
                          options={noYesOptions}
                          title={
                            <Translator
                              file="module-resourceManager"
                              name="resource.filed.away"
                            />
                          }
                          icon={<AppRemixIcon icon="inbox-archive" />}
                          value={values?.archived}
                          onChange={(v) => setFieldValue('archived', v)}
                          isClearable
                        />
                      </>
                    )}
                    <AppMultiSelect
                      icon={<AppRemixIcon icon="apps-2" />}
                      options={stateOptions}
                      title={
                        <Translator
                          file="module-resourceManager"
                          name={
                            view === 'resource'
                              ? 'resource.states'
                              : 'resource.asset.statements'
                          }
                        />
                      }
                      value={values.states}
                      onChange={(v) => setFieldValue('states', v)}
                      hasSearchBox
                      hasSelectOptions
                    />
                    {view === 'resource' && featureFilters?.length > 0 && (
                      <>
                        <FilterBlockTitle
                          onClick={() => setFieldFilters(!fieldFilters)}
                        >
                          <AppRemixIcon
                            icon={
                              fieldFilters ? 'arrow-down-s' : 'arrow-right-s'
                            }
                            filled
                          />
                          <Translator
                            file="module-legalCompliance"
                            name="filters.by.features"
                          />
                        </FilterBlockTitle>
                        <UnmountClosed isOpened={fieldFilters}>
                          {renderFeatureInputs(values, errors, setFieldValue)}
                        </UnmountClosed>
                      </>
                    )}
                  </>
                )}
              </Formik>
            )}
          </>
        )}
      </AuxSidebar>
    </>
  );
}

const FilterBlockTitle = styled.div`
  color: ${colors.primary};
  font-size: 14px;
  margin-top: 10px;
  cursor: pointer;
`;
